import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { Target, ClipboardCheck, ChevronRight, Clock } from "lucide-react";
import { statusOf, formatDate } from "@/lib/crm";

const DOT = { red: "bg-rose-500", yellow: "bg-amber-400", green: "bg-emerald-500", gray: "bg-stone-600" };

function tasksPendingLabel(t, n) {
  if (n === 1) return t("cc.tasks_pending_one");
  // Polish plural forms 2..4 vs 5+ handled roughly; english just uses n
  if (n >= 2 && n <= 4) return t("cc.tasks_pending_n").replace("{n}", n);
  return t("cc.tasks_pending_many").replace("{n}", n);
}

export default function CommandCenter() {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/career/command-center");
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;

  const goal = data?.active_goal;
  const nearest = data?.nearest_task;
  const openCount = data?.open_task_count || 0;

  const nearestDot = nearest?.due
    ? statusOf(nearest.due).color
    : "gray";

  return (
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dash-command-center">
      {/* Goal card */}
      <div
        className="paper p-5 border-amber-500/15 relative overflow-hidden"
        data-testid="cc-goal-card"
      >
        <div className="flex items-center gap-2">
          <Target className="text-amber-500" size={14} />
          <div className="mono text-[10px] tracking-[0.3em] text-amber-500 uppercase">
            {t("cc.goal_title")}
          </div>
        </div>
        {goal ? (
          <>
            <Link
              to={`/career/goals/${goal.goal_id}`}
              className="serif text-2xl text-stone-100 hover:text-amber-400 mt-2 block"
              data-testid="cc-goal-title"
            >
              {goal.title}
            </Link>
            {goal.next_step && (
              <div className="text-sm text-stone-300 mt-3">
                <span className="mono text-[10px] text-stone-500 uppercase tracking-widest">
                  {t("cc.next_step_label")}
                </span>{" "}
                <span className="text-stone-100">{goal.next_step.name}</span>
              </div>
            )}
            {(goal.steps || []).length > 0 && (
              <div className="mt-3">
                <div className="h-1.5 bg-stone-900 rounded overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all"
                    style={{ width: `${goal.progress_pct}%` }}
                  />
                </div>
                <div className="mt-1 mono text-[10px] text-stone-500 uppercase tracking-widest">
                  {goal.progress_pct}%
                </div>
              </div>
            )}
            <div className="mt-4">
              <Link
                to={`/career/goals/${goal.goal_id}`}
                data-testid="cc-goal-open"
                className="ghost-btn text-xs"
              >
                {t("cc.open")} <ChevronRight size={12} />
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="serif text-2xl text-stone-100 mt-2">{t("cc.no_active_goal")}</div>
            <p className="text-stone-400 text-sm mt-1">{t("cc.no_active_goal_cta")}</p>
            <Link to="/career" data-testid="cc-goal-open" className="ghost-btn text-xs mt-4 inline-flex">
              {t("path.open")} <ChevronRight size={12} />
            </Link>
          </>
        )}
      </div>

      {/* Ops tasks card */}
      <div
        className="paper p-5 border-amber-500/15 relative overflow-hidden"
        data-testid="cc-tasks-card"
      >
        <div className="flex items-center gap-2">
          <ClipboardCheck className="text-amber-500" size={14} />
          <div className="mono text-[10px] tracking-[0.3em] text-amber-500 uppercase">
            {t("cc.tasks_title")}
          </div>
        </div>
        {nearest ? (
          <>
            <div className="mt-2 flex items-start gap-2">
              <span className={`mt-2 inline-block w-2 h-2 rounded-full flex-shrink-0 ${DOT[nearestDot]}`} />
              <div>
                <div className="mono text-[10px] text-stone-500 uppercase tracking-widest">
                  {t("cc.next_task_label")}
                </div>
                <div className="serif text-xl text-stone-100 mt-1">{nearest.title}</div>
                {nearest.due && (
                  <div className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                    <Clock size={11} /> {formatDate(nearest.due, lang)}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 text-xs text-stone-400" data-testid="cc-tasks-count">
              {tasksPendingLabel(t, openCount)}
            </div>
          </>
        ) : (
          <div className="serif text-xl text-stone-100 mt-2">{t("cc.no_open_tasks")}</div>
        )}
        <div className="mt-4">
          <Link to="/ops/tasks" data-testid="cc-tasks-all" className="ghost-btn text-xs">
            {t("cc.see_all")} <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
